/* 迁移'@/pages/intelligence/pages/TrendingGames/components/GameSelect/index.tsx'组件并作修改
 *
 * URL-Based Approach for RoutePrompt Interception:
 *
 * This component supports two approaches for handling selection:
 *
 * 1. **State Approach (Legacy)**: When `urlParamKey` is NOT provided
 *    - Visual state updates immediately on selection
 *    - Parent component manages state directly
 *    - No RoutePrompt interception
 *
 * 2. **URL Approach (New)**: When `urlParamKey` IS provided
 *    - Selection updates URL via `history.push()` instead of updating visual state immediately
 *    - Visual state syncs from `defaultUnifiedId` prop (derived from URL)
 *    - This enables RoutePrompt to intercept navigation before data changes
 *    - Visual state only updates after RoutePrompt confirms and URL actually changes
 *    - Parent component syncs state from URL parameters, ensuring data only changes after confirmation
 *
 * Key Props for URL Approach:
 * - `urlParamKey`: URL parameter name (e.g., 'project_id', 'studio_id')
 * - `urlBasePath`: Base path for URL updates (defaults to current pathname)
 * - `urlUpdateCondition`: Optional function to check if URL should be updated
 * - `defaultUnifiedId`: Current value from URL (used to sync visual state)
 *
 * Flow for URL Approach:
 * 1. User clicks item → `handleGameSelect`/`handleImageClick` called
 * 2. `onChange` called first (for buried points, etc.)
 * 3. URL updated via `history.push()` with new parameter value
 * 4. RoutePrompt intercepts navigation if there are unsaved changes
 * 5. If confirmed, URL changes → `defaultUnifiedId` prop updates
 * 6. `useEffect` syncs visual state from `defaultUnifiedId`
 * 7. Parent component syncs data from URL parameters
 *
 * This ensures that page data and visual selection only change after RoutePrompt confirmation.
 */

import React, { useEffect, useState, useRef } from 'react';
import { useLocation, history, Location } from 'umi';
import './index.scss';
import classnames from 'classnames';
import { Bubble } from 'tea-component';
// import GameImage from '@home/components/GameImage';
import GameImage from '@sg/components/GameSelectAll/GameImage';
import SwiperBtn from '@intelligence/components/SwiperBtn';
import SearchSelect from '@sg/components/GameSelectAll/SearchSelect';
import useSlideEffect from '@intelligence/pages/TrendingGames/components/GameSelect/_hooks/useSlideEffect';
import Loading from '@intelligence/components/Loading';

import '@intelligence/pages/TrendingGames/components/GameSelect/index.scss';

import type { IBaseProps } from '@/pages/intelligence/types/common';
import type { ITrendingGame } from '@intelligence/pages/TrendingGames/types';
import { getStudiosPermission } from '@sg/_config/permission';
import GameSelectAllImg from '@sg/assests/images/game-select-all.png';

interface IGameSelectProps extends IBaseProps {
  // Data fetching function that returns transformed ITrendingGame[]
  fetchData: () => Promise<ITrendingGame[]>;
  // Loading text
  loadingText?: string;
  // Auto-select first item when data loads
  autoSelectFirst?: boolean;
  // Selection change callback (called before URL update for backward compatibility)
  onChange: (payload: ITrendingGame, triggerSource?: 'auto' | 'click') => void;
  // Optional callback when data is loaded
  onDataLoaded?: (data: ITrendingGame[]) => void;
  // Default unified_id to select (overrides autoSelectFirst)
  defaultUnifiedId?: string;
  // Optional: URL param key to update when selection changes (e.g., 'project_id', 'studio_id')
  // If provided, GameSelect will update this URL param using history.push() to enable RoutePrompt interception
  urlParamKey?: string;
  // Optional: Base path for URL updates (defaults to current pathname)
  urlBasePath?: string;
  // Optional: Condition function to check if URL should be updated
  // Returns true if URL should be updated, false otherwise
  urlUpdateCondition?: (location: Location) => boolean;
  // Optional: parent-controlled URL builder (e.g. Live Titles canonical URLs)
  onBuildUrl?: (location: Location, unifiedId: string) => string;
}

const GameSelectAll: React.FC<IGameSelectProps> = (props) => {
  const {
    style,
    className,
    fetchData,
    loadingText,
    autoSelectFirst = true,
    onChange,
    onDataLoaded,
    defaultUnifiedId,
    urlParamKey,
    urlBasePath,
    urlUpdateCondition,
    onBuildUrl,
  } = props;

  const location = useLocation();

  // Internal state
  const [options, setOptions] = useState<ITrendingGame[]>([]);
  const [value, setValue] = useState('');
  const [selectedUidAndId, setSelectedUidAndId] = useState('');
  // 默认 ALL 被选中：只有用户手动选择或 URL 存在匹配项时才切到具体项
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [loading, setLoading] = useState(false);
  // to force a layout refresh
  const [layoutRefresh, setLayoutRefresh] = useState(0);
  // Track if we've done initial selection notification to parent
  const hasInitialSelectionNotified = useRef(false);

  // 构造 "ALL" 选项 payload，供 onChange 通知父组件时使用
  const buildAllPayload = (): ITrendingGame =>
    ({ unified_id: 'all', id: '', name: 'All', isAll: true } as unknown as ITrendingGame);

  const {
    // 容器dom
    containerRef,
    // 列表容器dom
    listContainerRef,
    // 是否超宽，导致溢出隐藏、滚动
    isOverflow,
    // 左右滑动按钮禁用状态
    slideDisabledState,
    // 点击左滑按钮的回调
    handleSlideLeft,
    // 点击右滑按钮的回调
    handleSlideRight,
  } = useSlideEffect({
    /**
     * REMARK: 这里10、10分别是左右滑动按钮的宽度、margin，100为All按钮的宽度、内边距之和，20是All按钮的margin-left
     */
    othersWidth: (10 + 10) * 2 + 100 + 20,
    // 重新计算宽度的依赖 - using options and layoutRefresh instead of layoutRefreshDeps
    updateDeps: [options, layoutRefresh],
  });

  const handleSlideIntoView = (index: number) => {
    const optionNode = listContainerRef.current?.children?.[index];
    if (!optionNode) return;
    optionNode.scrollIntoView({
      inline: 'center',
      block: 'nearest', // only scroll horizontally if needed
      behavior: 'smooth',
    });
  };

  const handleGameSelect = (payload: ITrendingGame) => {
    const optionIdx = options?.findIndex(
      (option) => option?.unified_id + option?.id === payload?.unified_id + payload?.id,
    );
    if (optionIdx !== -1) {
      const option = options?.[optionIdx];

      if (urlParamKey) {
        // URL approach: Don't update visual state here - it will sync from defaultUnifiedId prop when URL changes
        // This allows RoutePrompt to intercept before visual state changes
        handleSlideIntoView(optionIdx);

        // Call onChange first for backward compatibility (e.g., buried point tracking)
        onChange(option, 'click');

        // Update URL to trigger RoutePrompt interception
        // Check condition if provided
        if (urlUpdateCondition && !urlUpdateCondition(location)) {
          return;
        }

        const nextUrl = onBuildUrl
          ? onBuildUrl(location, option.unified_id)
          : (() => {
            const params = new URLSearchParams(location.search);
            params.set(urlParamKey, option.unified_id);
            const basePath = urlBasePath || location.pathname;
            return `${basePath}?${params.toString()}`;
          })();
        history.push(nextUrl);
      } else {
        // State approach: Update visual state immediately (old behavior)
        setIsAllSelected(false);
        setValue(option.unified_id);
        setSelectedUidAndId(option.unified_id + option.id);
        handleSlideIntoView(optionIdx);

        // Call onChange after updating visual state
        onChange(option);
      }
    }
  };

  const handleImageClick = (option: ITrendingGame) => {
    if (urlParamKey) {
      // URL approach: Don't update visual state here - it will sync from defaultUnifiedId prop when URL changes
      // This allows RoutePrompt to intercept before visual state changes

      // Call onChange first for backward compatibility (e.g., buried point tracking)
      onChange(option, 'click');

      // Update URL to trigger RoutePrompt interception
      // Check condition if provided
      if (urlUpdateCondition && !urlUpdateCondition(location)) {
        return;
      }

      const nextUrl = onBuildUrl
        ? onBuildUrl(location, option.unified_id)
        : (() => {
          const params = new URLSearchParams(location.search);
          params.set(urlParamKey, option.unified_id);
          const basePath = urlBasePath || location.pathname;
          return `${basePath}?${params.toString()}`;
        })();
      history.push(nextUrl);
    } else {
      // State approach: Update visual state immediately (old behavior)
      setIsAllSelected(false);
      setValue(option.unified_id);
      setSelectedUidAndId(option.unified_id + option.id);

      // Call onChange after updating visual state
      onChange(option);
    }
  };

  // 点击 ALL：回到默认 ALL 状态
  const handleAllClick = () => {
    const allPayload = buildAllPayload();

    if (urlParamKey) {
      // URL 模式：先通知父组件（埋点等），再设置 urlParamKey=all
      onChange(allPayload, 'click');

      if (urlUpdateCondition && !urlUpdateCondition(location)) {
        return;
      }

      const nextUrl = onBuildUrl
        ? onBuildUrl(location, 'all')
        : (() => {
          const params = new URLSearchParams(location.search);
          params.set(urlParamKey, 'all');
          const basePath = urlBasePath || location.pathname;
          return `${basePath}?${params.toString()}`;
        })();
      history.push(nextUrl);
    } else {
      setIsAllSelected(true);
      setValue('');
      setSelectedUidAndId('');
      onChange(allPayload);
    }
  };

  // New useEffect to trigger a recalculation after the DOM updates
  useEffect(() => {
    // After options are set, wait briefly for the DOM to render the new items,
    // then trigger a refresh to force useSlideEffect to recalculate.
    const timer = setTimeout(() => setLayoutRefresh((c) => c + 1), 100);
    return () => clearTimeout(timer);
  }, [options]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await fetchData();
        // setOptions(data);
        const filterdData = data.filter((item: any) => getStudiosPermission('studio_list', item?.unified_id));
        setOptions(filterdData);
        // Call onDataLoaded callback if provided
        onDataLoaded?.(filterdData);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // Only run once on mount

  // Sync visual state from defaultUnifiedId prop (which comes from URL)
  // This ensures visual state only changes when URL actually changes (after RoutePrompt confirms)
  useEffect(() => {
    if (!options || options.length === 0) {
      return;
    }

    // 只有 URL/props 提供了 defaultUnifiedId 且能匹配到具体项时，才切到该项；
    // 如果 defaultUnifiedId === 'all'，则保持 ALL 选中态。
    // 其余情况一律回到 ALL 默认态。autoSelectFirst 已不再影响默认选择。
    let gameToSelect: ITrendingGame | null = null;
    if (defaultUnifiedId && defaultUnifiedId !== 'all') {
      gameToSelect = options.find((game: ITrendingGame) => game.unified_id === defaultUnifiedId) || null;
    }

    if (gameToSelect) {
      const newValue = gameToSelect.unified_id;
      const newSelectedUidAndId = gameToSelect.unified_id + gameToSelect.id;

      setIsAllSelected(false);
      setValue((prevValue) => (prevValue !== newValue ? newValue : prevValue));
      setSelectedUidAndId((prevSelectedUidAndId) => {
        if (prevSelectedUidAndId !== newSelectedUidAndId) {
          // Notify parent on initial selection only (when options first load)
          if (!hasInitialSelectionNotified.current) {
            hasInitialSelectionNotified.current = true;
            onChange(gameToSelect as ITrendingGame, 'auto');
          }
          return newSelectedUidAndId;
        }
        return prevSelectedUidAndId;
      });
    } else {
      // 默认 ALL 状态（包括 defaultUnifiedId === 'all'）
      setIsAllSelected(true);
      setValue('');
      setSelectedUidAndId('');
    }
  }, [defaultUnifiedId, options, autoSelectFirst, onChange]);

  // Auto-scroll to selected item whenever selectedUidAndId changes
  useEffect(() => {
    if (selectedUidAndId && listContainerRef.current) {
      const optionIdx = options?.findIndex((option) => option?.unified_id + option?.id === selectedUidAndId);
      if (optionIdx !== -1) {
        handleSlideIntoView(optionIdx); // auto-scroll behavior whenever selectedUidAndId changes
      }
    }
  }, [selectedUidAndId, listContainerRef.current]);

  // Show loading state
  if (loading) {
    return <Loading content={loadingText} style={{ height: '110px' }} />; // height of this component
  }

  const hasGameOptions = (options?.length ?? 0) > 0;

  return (
    <div
      ref={containerRef}
      style={style}
      className={classnames([
        'dbi-trending-heading dbs-game-select-all',
        'dbi-swipeable-game-select',
        'dbs-game-select',
        className,
      ])}
    >
      {/* ALL 按钮：默认选中，点击后清空具体选择 */}
      <div
        className={classnames('dbs-game-select-all-btn-wrap rainbow-avatar-swiper-item', {
          'dbs-game-select-all-btn-wrap--solo': !hasGameOptions,
        })}
      >
        <div
          className={classnames('dbs-game-select-all-btn', {
            'rainbow-avatar-img-chosen': isAllSelected,
            'rainbow-avatar-img-unchosen': !isAllSelected,
          })}
          onClick={handleAllClick}
        >
          <GameImage className="dbs-game-select-all-btn__img" src={GameSelectAllImg} entityType="" />
          <span className="dbs-game-select-all-btn__text">ALL</span>
        </div>
      </div>

      {hasGameOptions && (
        <>
          {isOverflow && (
            <SwiperBtn
              gradient
              className={classnames('dbi-swipeable-game-select__swipe-btn', 'dbi-swipeable-game-select__swipe-btn--left')}
              type="prev"
              disabled={slideDisabledState.prev}
              onClick={handleSlideLeft}
            />
          )}
          <div ref={listContainerRef} className="rainbow-avatar-swiper">
            {options?.map((option) => {
              const isSelected = selectedUidAndId === option?.unified_id + option?.id;
              const entityType = option?.type;
              return (
                <div key={`${option.unified_id}-${option.id}`} className="rainbow-avatar-swiper-item">
                  <Bubble
                    placement="bottom"
                    content={`${option?.name}`}
                    className="dbs-rainbow-avatar-swiper-item-bubble"
                    openDelay={400}
                  >
                    <div className="rainbow-avatar-swiper-item-wrap">
                      <GameImage
                        hasImageLoading={true}
                        className={classnames({
                          'rainbow-avatar-img-chosen': isSelected,
                          'rainbow-avatar-img-unchosen': !isSelected,
                          [`rainbow-avatar-img-chosen-${entityType}`]: isSelected,
                          [`rainbow-avatar-img-unchosen-${entityType}`]: !isSelected,
                        })}
                        entityType={entityType}
                        src={option?.cover}
                        onClick={() => handleImageClick(option)}
                      />
                    </div>
                  </Bubble>
                </div>
              );
            })}
          </div>
          {isOverflow && (
            <SwiperBtn
              gradient
              className={classnames('dbi-swipeable-game-select__swipe-btn', 'dbi-swipeable-game-select__swipe-btn--right')}
              type="next"
              disabled={slideDisabledState.next}
              onClick={handleSlideRight}
            />
          )}
          <SearchSelect
            className="dbi-swipeable-game-select__search-select"
            options={options}
            value={value}
            selectedUidAndId={selectedUidAndId}
            onSelect={handleGameSelect}
          />
        </>
      )}
    </div>
  );
};

export default GameSelectAll;
